import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot,
  MapPin, CheckCircle2, ArrowRight, Crown, ArrowLeft
} from 'lucide-react';
import { TRANSPORT_TYPES } from './constants';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

const iconMap = {
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot
};

// Transport selector component (moved outside to prevent re-creation on each render)
const TransportSelector = ({ value, onChange, language }) => (
  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 w-full">
    {Object.entries(TRANSPORT_TYPES).map(([key, config]) => {
      const IconComponent = iconMap[config.icon];
      const isSelected = value === key;
      return (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`
            flex flex-col items-center gap-0.5 sm:gap-1 p-1.5 sm:p-3 rounded-lg border-2 transition-all min-w-0 w-full
            ${isSelected 
              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }
          `}
        >
          <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isSelected ? 'text-orange-500' : 'text-slate-500'}`} />
          <span className={`text-[9px] sm:text-xs text-center leading-tight truncate w-full ${isSelected ? 'text-orange-600 font-medium' : 'text-slate-600'}`}>
            {config.label[language] || config.label['pt-BR']}
          </span>
        </button>
      );
    })}
  </div>
);

const WelcomeModal = ({ 
  open, 
  onOpenChange, 
  trip,
  isOrganizer,
  memberInfo,
  onSave,
  onLater
}) => {
  const { t, language } = useLanguage();
  const locale = language === 'pt-BR' ? ptBR : enUS;
  
  // State
  const [isTraveling, setIsTraveling] = useState(memberInfo?.is_traveling ?? true);
  const [outboundTransportType, setOutboundTransportType] = useState(memberInfo?.outbound_transport_type || memberInfo?.transport_type || null);
  const [returnTransportType, setReturnTransportType] = useState(memberInfo?.return_transport_type || memberInfo?.transport_type || null);
  const [attendingDays, setAttendingDays] = useState(memberInfo?.attending_days || []);
  const [activeTab, setActiveTab] = useState('outbound');

  // Sync state when modal opens or memberInfo changes
  useEffect(() => {
    if (open) {
      setIsTraveling(memberInfo?.is_traveling ?? true);
      setOutboundTransportType(memberInfo?.outbound_transport_type || memberInfo?.transport_type || null);
      setReturnTransportType(memberInfo?.return_transport_type || memberInfo?.transport_type || null);
      setAttendingDays(memberInfo?.attending_days || []);
    }
  }, [open, memberInfo]);

  // Generate trip days
  const tripDays = React.useMemo(() => {
    if (!trip?.start_date || !trip?.end_date) return [];
    try {
      const start = typeof trip.start_date === 'string' ? parseISO(trip.start_date) : new Date(trip.start_date);
      const end = typeof trip.end_date === 'string' ? parseISO(trip.end_date) : new Date(trip.end_date);
      return eachDayOfInterval({ start, end });
    } catch {
      return [];
    }
  }, [trip?.start_date, trip?.end_date]);

  // Toggle day selection
  const toggleDay = (dateStr) => {
    setAttendingDays(prev => 
      prev.includes(dateStr) 
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr]
    );
  };

  // Select all days
  const selectAllDays = () => {
    setAttendingDays(tripDays.map(d => format(d, 'yyyy-MM-dd')));
  };

  // Handle save
  const handleSave = () => {
    onSave({
      is_traveling: isOrganizer ? isTraveling : true,
      outbound_transport_type: outboundTransportType,
      return_transport_type: returnTransportType,
      // Keep legacy field for backwards compatibility
      transport_type: outboundTransportType,
      attending_days: attendingDays,
      is_completed: false
    });
  };

  // Don't show for organizer who answered they won't travel
  const showForm = !isOrganizer || isTraveling;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col !p-0 !mx-0 w-full sm:w-auto">
        <DialogHeader className="p-3 sm:p-6 pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0" />
            <span className="truncate">{t('myTrip.welcome.title')}</span>
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {t('myTrip.welcome.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 sm:px-6">
          <div className="space-y-4 sm:space-y-5 pb-4 max-w-full">
            {/* Trip info (read-only) */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm min-w-0">
                <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-500 flex-shrink-0">{t('myTrip.welcome.origin')}:</span>
                <span className="font-medium break-words flex-1 min-w-0">{trip?.origin || '-'}</span>
              </div>
              <div className="flex items-center justify-center">
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="flex items-start gap-2 text-sm min-w-0">
                <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-500 flex-shrink-0">{t('myTrip.welcome.destination')}:</span>
                <span className="font-medium break-words flex-1 min-w-0">{trip?.destination || '-'}</span>
              </div>
            </div>

            {/* Organizer question */}
            {isOrganizer && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Crown className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{t('myTrip.welcome.organizerQuestion')}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{t('myTrip.welcome.organizerQuestionDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Switch
                    checked={isTraveling}
                    onCheckedChange={setIsTraveling}
                    className="flex-shrink-0"
                  />
                </div>
              </div>
            )}

            {showForm && (
              <>
                {/* Transport type - tabs for outbound and return */}
                <div className="max-w-full">
                  <Label className="text-sm sm:text-base font-medium">{t('myTrip.welcome.transportLabel')}</Label>
                  <p className="text-xs sm:text-sm text-slate-500 mb-3">{t('myTrip.welcome.transportDesc')}</p>
                  
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-3 h-auto">
                      <TabsTrigger value="outbound" className="flex items-center justify-center gap-1 text-[11px] sm:text-sm py-2 px-1 sm:px-3 min-w-0">
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{t('myTrip.transport.outbound')}</span>
                      </TabsTrigger>
                      <TabsTrigger value="return" className="flex items-center justify-center gap-1 text-[11px] sm:text-sm py-2 px-1 sm:px-3 min-w-0">
                        <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{t('myTrip.transport.return')}</span>
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="outbound" className="mt-0">
                      <TransportSelector 
                        value={outboundTransportType} 
                        onChange={setOutboundTransportType}
                        language={language}
                      />
                      {outboundTransportType && (
                        <Badge className="mt-2 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                          {TRANSPORT_TYPES[outboundTransportType]?.label[language]}
                        </Badge>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="return" className="mt-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="same-transport"
                          checked={returnTransportType === outboundTransportType}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setReturnTransportType(outboundTransportType);
                            }
                          }}
                        />
                        <label htmlFor="same-transport" className="text-sm text-slate-600 dark:text-slate-400">
                          {t('myTrip.transport.sameAsOutbound')}
                        </label>
                      </div>
                      <TransportSelector 
                        value={returnTransportType} 
                        onChange={setReturnTransportType}
                        language={language}
                      />
                      {returnTransportType && (
                        <Badge className="mt-2 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          {TRANSPORT_TYPES[returnTransportType]?.label[language]}
                        </Badge>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Attending days - horizontal scroll on mobile */}
                <div className="max-w-full">
                  <div className="flex items-start sm:items-center justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <Label className="text-sm sm:text-base font-medium">{t('myTrip.welcome.daysLabel')}</Label>
                      <p className="text-xs sm:text-sm text-slate-500">{t('myTrip.welcome.daysDesc')}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={selectAllDays}
                      className="text-orange-500 hover:text-orange-600 text-[10px] sm:text-sm flex-shrink-0 px-2 h-7"
                    >
                      {t('myTrip.welcome.selectAll')}
                    </Button>
                  </div>
                  
                  {/* Mobile: horizontal scroll, Desktop: grid */}
                  <div className="sm:hidden overflow-x-auto overflow-y-hidden">
                    <div className="flex gap-2 pb-2 pr-1" style={{ minWidth: 'max-content' }}>
                      {tripDays.map((day) => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const isSelected = attendingDays.includes(dateStr);
                        return (
                          <div
                            key={dateStr}
                            onClick={() => toggleDay(dateStr)}
                            className={`
                              flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all flex-shrink-0
                              ${isSelected 
                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                                : 'border-slate-200 dark:border-slate-700'
                              }
                            `}
                            style={{ minWidth: '100px' }}
                          >
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleDay(dateStr)}
                            />
                            <div>
                              <p className={`text-sm font-medium ${isSelected ? 'text-orange-600' : ''}`}>
                                {format(day, 'EEE', { locale })}
                              </p>
                              <p className="text-xs text-slate-500">
                                {format(day, 'dd/MM', { locale })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Desktop: grid */}
                  <div className="hidden sm:grid grid-cols-3 gap-2">
                    {tripDays.map((day) => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const isSelected = attendingDays.includes(dateStr);
                      return (
                        <div
                          key={dateStr}
                          onClick={() => toggleDay(dateStr)}
                          className={`
                            flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                            ${isSelected 
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                            }
                          `}
                        >
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => toggleDay(dateStr)}
                          />
                          <div>
                            <p className={`text-sm font-medium ${isSelected ? 'text-orange-600' : ''}`}>
                              {format(day, 'EEE', { locale })}
                            </p>
                            <p className="text-xs text-slate-500">
                              {format(day, 'dd/MM', { locale })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {attendingDays.length > 0 && (
                    <Badge variant="secondary" className="mt-2">
                      {attendingDays.length} {attendingDays.length === 1 ? t('common.day') : t('common.days')} {t('myTrip.welcome.selected')}
                    </Badge>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 p-3 sm:p-6 pt-3 sm:pt-4 border-t bg-white dark:bg-slate-900 flex-shrink-0">
          <Button
            variant="outline"
            className="flex-1 rounded-full text-xs sm:text-sm h-9 sm:h-10"
            onClick={onLater}
          >
            {t('myTrip.welcome.later')}
          </Button>
          <Button
            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm h-9 sm:h-10"
            onClick={handleSave}
            disabled={showForm && (!outboundTransportType || attendingDays.length === 0)}
          >
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
